<template>
  <v-dialog
    :value="value"
    max-width="720"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-toolbar color="primary" dark flat dense>
        <v-toolbar-title>{{
          $t("reports.aiClient.guideTitle")
        }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pt-5" style="max-height: 70vh">
        <div class="grey--text font-weight-medium mb-1">
          {{ $t("reports.aiClient.guideStep") }} {{ step + 1 }}
          {{ $t("reports.aiClient.guideOf") }} {{ steps.length }}
        </div>
        <h2 class="green--text text--darken-1 mb-3">{{ current.title }}</h2>
        <p class="black--text mb-4">{{ current.intro }}</p>

        <ol class="guide-points">
          <li v-for="(pt, i) in current.points" :key="i" class="mb-3">
            <span class="font-weight-bold black--text">{{
              pointHeading(pt)
            }}</span>
            <span class="grey--text text--darken-2">{{ pointBody(pt) }}</span>
          </li>
        </ol>

        <div v-if="current.images.length" class="mt-2">
          <div
            v-for="(img, i) in current.images"
            :key="i"
            class="guide-shot mb-3"
          >
            <img :src="imgUrl(img)" class="guide-img" :alt="current.title" />
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="py-3 px-4">
        <v-spacer />
        <v-btn v-if="step > 0" outlined color="primary" @click="prev">{{
          $t("reports.aiClient.guideBack")
        }}</v-btn>
        <v-btn v-if="step === 0" outlined color="primary" @click="close">{{
          $t("reports.aiClient.guideClose")
        }}</v-btn>
        <v-btn v-if="!isLast" outlined color="primary" @click="close">{{
          $t("reports.aiClient.guideSkip")
        }}</v-btn>
        <v-btn v-if="!isLast" color="primary" depressed @click="next">{{
          $t("reports.aiClient.guideNext")
        }}</v-btn>
        <v-btn v-else color="primary" depressed @click="close">{{
          $t("reports.aiClient.guideDone")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * 4-step onboarding walkthrough for the client AI Reports dashboard. Content is
 * localized under reports.aiClient.g{n}Title / g{n}Intro / g{n}Points.
 */
// Illustrative screenshots per step (in public/img/ai-guide/).
const STEP_IMAGES = {
  1: ["Step-1-image-1.png", "Step-1-image-2.png"],
  2: ["Step-2.png"],
  3: ["Step-3.png"],
  4: ["Step-4.png"],
};

export default {
  name: "DashboardGuideDialog",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return { step: 0, publicPath: process.env.BASE_URL || "/" };
  },
  computed: {
    steps() {
      const t = (k) => this.$t(`reports.aiClient.${k}`);
      return [1, 2, 3, 4].map((n) => ({
        title: t(`g${n}Title`),
        intro: t(`g${n}Intro`),
        points: t(`g${n}Points`),
        images: STEP_IMAGES[n] || [],
      }));
    },
    current() {
      return (
        this.steps[this.step] || {
          title: "",
          intro: "",
          points: [],
          images: [],
        }
      );
    },
    isLast() {
      return this.step === this.steps.length - 1;
    },
  },
  watch: {
    value(open) {
      if (open) this.step = 0;
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    next() {
      if (!this.isLast) this.step += 1;
    },
    prev() {
      if (this.step > 0) this.step -= 1;
    },
    pointHeading(pt) {
      const idx = String(pt).indexOf(":");
      return idx > -1 ? String(pt).slice(0, idx + 1) + " " : "";
    },
    pointBody(pt) {
      const idx = String(pt).indexOf(":");
      return idx > -1
        ? String(pt)
            .slice(idx + 1)
            .trim()
        : String(pt);
    },
    imgUrl(name) {
      return `${this.publicPath}img/ai-guide/${name}`;
    },
  },
};
</script>

<style scoped>
.guide-points {
  padding-left: 18px;
}
.guide-shot {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
}
.guide-img {
  display: block;
  width: 100%;
  height: auto;
}
</style>
